import { Trans, useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";

/**
 * "This manual is published — continue editing?" — the gate in front of a `confirmed`/`marked` manual (ADR-018).
 *
 * **Deliberately NOT a react-aria `Modal`** (owner bug report, 2026-09-02). As a nested `ModalOverlay` inside the
 * editor's own overlay, clicking t("manualEditor.editAnyway") dismissed the EDITOR: the author landed back on the facility manuals
 * view and only a page refresh let them in again. Rendering the gate in-tree fixed it, confirmed by the owner.
 *
 * ⚠ **The mechanism below is the original diagnosis and is NOT confirmed** — recorded honestly because the obvious
 * reading turned out to be wrong. The theory was that a `Modal` portals outside the editor's subtree, so the click
 * read as an *outside* click and the editor dismissed itself. But `AssetLibraryDrawer` is nested exactly the same way
 * and the owner verified in a real browser (2026-09-02) that picking a file, closing the 👁 preview, and pressing
 * Escape on it all leave you in the editor. react-aria maintains an overlay stack and ignores outside-interaction on
 * a non-topmost overlay, which is very likely why. So whatever made THIS component bounce, it was probably not the
 * nesting alone — do not generalise from it (a jsdom "reproduction" built on that assumption proved to be an
 * artifact; #245). What remains true is that this gate works, and that it does not need to be a modal at all:
 * editing behind it is already frozen (`needsAck` forces `readOnly`), so it is a decision gate, not a focus-stealer.
 *
 */
export function PublishedManualGate({
  manualName,
  state,
  onCancel,
  onContinue,
}: {
  manualName: string;
  state: "confirmed" | "marked";
  onCancel: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const continueRef = useRef<HTMLButtonElement>(null);

  // Move focus to the primary action on mount, and let Escape cancel — the two things the Modal used to give us. Focus
  // is NOT trapped: the editor behind is read-only, so tabbing out of the gate cannot change anything.
  useEffect(() => {
    continueRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation(); // …and never let it reach the editor overlay, which would ALSO close (that was the bug)
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="false"
        aria-labelledby="published-gate-title"
        aria-describedby="published-gate-body"
        className="border-border bg-background w-[min(30rem,100%)] rounded-xl border p-5 shadow-xl"
      >
        <h2 id="published-gate-title" className="text-base font-semibold">
          {t("manualEditor.thisManualIsPublished")}
        </h2>
        <p id="published-gate-body" className="text-muted-foreground mt-1 text-sm">
          <Trans
            i18nKey="manualEditor.publishedBody"
            values={{ state: t(state === "marked" ? "manualEditor.stateMarked" : "manualEditor.stateConfirmed") }}
            components={{
              name: <span className="text-foreground font-medium">{manualName}</span>,
              live: <span className="text-foreground font-medium">{t("manualEditor.live")}</span>,
            }}
          />
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onPress={onCancel}>
            {t("manualEditor.cancel")}
          </Button>
          <Button ref={continueRef} variant="default" size="sm" onPress={onContinue}>
            {t("manualEditor.editAnyway")}
          </Button>
        </div>
      </div>
    </div>
  );
}
